import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { sanitizeQueryParam } from '@v2/lib';
import '@v2/styles/tailwind.css';
import { AccountsTable, AccountDetails } from './components';
import { Account } from '@v2/types';
import { useAccounts, useAccountDetails, useTableFilters } from '@v2/hooks';
import { AccountForm } from '@v2/features';
import { TableFilters } from '@v2/components';
import type { ActionMenuItem } from '@v2/components';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPen } from '@fortawesome/free-solid-svg-icons';

const AccountsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const selectedAccountId = sanitizeQueryParam(searchParams.get('accountId'));

  const {
    accounts,
    loading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useAccounts();

  const {
    filteredData,
    search,
    setSearch,
    sortConfig,
    handleSort,
  } = useTableFilters({
    data: accounts,
    searchKeys: ['firstName', 'lastName', 'email'],
  });

  const {
    account,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = useAccountDetails(selectedAccountId);

  const handleSelectAccount = (selectedAccount: Account) => {
    searchParams.set('accountId', selectedAccount.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('accountId');
    history.push({ search: searchParams.toString() });
  };

  const handleCreateAccount = () => {
    setIsCreating(true);
    setIsFormOpen(true);
  };

  const handleEditAccount = () => {
    setIsCreating(false);
    setIsFormOpen(true);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setIsCreating(false);
  };

  const handleFormCompleted = (newAccountId?: string) => {
    setIsFormOpen(false);
    setIsCreating(false);

    refetchAccounts();

    if (newAccountId) {
      searchParams.set('accountId', newAccountId);
      history.push({ search: searchParams.toString() });
    } else {
      refetchDetails();
    }
  };

  return (
    <>
      <SettingsPageLayout
        icon={faUser}
        title="Accounts"
        actionLabel="Create Account"
        onAction={handleCreateAccount}
        filters={
          <TableFilters
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name or email..."
          />
        }
      >
        <ListDetailView
          selectedId={selectedAccountId}
          list={
            <AccountsTable
              accounts={filteredData}
              loading={accountsLoading}
              error={accountsError}
              onSelectAccount={handleSelectAccount}
              selectedAccountId={selectedAccountId || undefined}
              sortConfig={sortConfig}
              onSortChange={handleSort}
            />
          }
          detail={
            <DetailPanel
              title="Account Details"
              onClose={handleClosePanel}
              actions={
                account
                  ? ([
                      {
                        key: 'edit',
                        label: 'Edit account',
                        icon: <FontAwesomeIcon icon={faPen} />,
                        onClick: handleEditAccount,
                      },
                    ] as ActionMenuItem[])
                  : undefined
              }
              loading={detailsLoading}
              loadingMessage="Loading account details..."
              error={detailsError}
              errorMessage="There was an error fetching the account details."
              onRetry={refetchDetails}
            >
              {account && (
                <AccountDetails account={account} />
              )}
            </DetailPanel>
          }
        />
      </SettingsPageLayout>

      {isFormOpen && (
        <AccountForm
          account={isCreating ? null : account}
          onCancel={handleCancelForm}
          onCompleted={handleFormCompleted}
        />
      )}
    </>
  );
};

export default AccountsPage;
